import { ErrorMessage, Field, Form, Formik } from "formik";
import { LockSimple } from "@phosphor-icons/react";
import { FC, useState } from "react";
import * as yup from "yup";
import { motion } from "framer-motion";
import useUpdateAccount from "../hooks/useUpdateAccount";
import { useNavigate } from "react-router-dom"; 
import { useAuth } from "../context/AuthContext"; 

const schema = yup.object().shape({
  oldPassword: yup.string().required("Old password is required"),
  newPassword: yup
    .string()
    .min(6, "Password must be at least 8 characters long")
    .required("New password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], "Passwords must match")
    .required("Confirm password is required"),
});

const ChangePasswordModal: FC<{
  setShowPasswordModal: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ setShowPasswordModal }) => {
  const [passwordIsVisible, setPasswordIsVisible] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth(); // [3] Destructure logout

  const {
      isUpdatingAccountError,
      updateAccountError,
      updateAccount,
      updatingAccount,
    } = useUpdateAccount(
      {}, 
      { autoUpdateUser: false } 
    );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed top-0 left-0 bg-deepBlue/80 backdrop-blur-sm w-full h-screen grid place-items-center p-5 z-50"
    >
      <Formik
        validationSchema={schema}
        initialValues={{
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        }}
        onSubmit={(values) => {
          updateAccount(
            {
              password: values.newPassword,
            },
            {
              onError(error) {
                console.log(error);
              },
              // [5] Handle the success flow here manually
              onSuccess() {
                setShowPasswordModal(false);
                logout(); // Clear token and user state
                navigate("/login"); // Send to login
              },
            }
          );
        }}
      >
        {({ dirty, isValid }) => {
          return (
            <Form className="bg-white max-w-sm w-full p-5 rounded-xl shadow-sm flex items-center flex-col gap-5">
              <div className="p-2 bg-gray-100 rounded-full ring-8 ring-gray-50">
                <LockSimple size={28} />
              </div>
              <div className="flex items-center flex-col">
                <div className="font-medium">Change Password</div>
                <p className="text-gray-500 text-center">
                  Enter your password to make this change.
                </p>
              </div>

              {/* [UI Inputs remain exactly the same as your code...] */}
              
              <div className="flex flex-col gap-2 max-w-md w-full">
                <label className="text-sm font-bold text-gray-700">Old Password*</label>
                <div className="rounded-lg border border-gray-300 shadow flex items-center w-full relative overflow-hidden focus-within:outline-deepBlue focus-within:outline accent-deepBlue">
                  <Field
                    autoComplete="off"
                    type={passwordIsVisible ? "text" : "password"}
                    name="oldPassword"
                    placeholder="Enter your old password"
                    className="px-4 py-3 bg-transparent flex-1 rounded-lg focus:outline-none"
                  />
                   <div onClick={() => setPasswordIsVisible((prev) => !prev)} className="absolute right-0 m-4 cursor-pointer text-sm">
                    {passwordIsVisible ? "Hide" : "Show"}
                  </div>
                </div>
                <ErrorMessage name="oldPassword" component="div" className="text-red-500" />
              </div>

              <div className="flex flex-col gap-2 max-w-md w-full">
                <label className="text-sm font-bold text-gray-700">New Password*</label>
                <div className="rounded-lg border border-gray-300 shadow flex items-center w-full relative overflow-hidden focus-within:outline-deepBlue focus-within:outline accent-deepBlue">
                  <Field
                    autoComplete="off"
                    type={passwordIsVisible ? "text" : "password"}
                    name="newPassword"
                    placeholder="Create a new password"
                    className="px-4 py-3 bg-transparent flex-1 rounded-lg focus:outline-none"
                  />
                </div>
                 <ErrorMessage name="newPassword" component="div" className="text-red-500" />
              </div>

              <div className="flex flex-col gap-2 max-w-md w-full">
                <label className="text-sm font-bold text-gray-700">Confirm New Password*</label>
                <div className="rounded-lg border border-gray-300 shadow flex items-center w-full relative overflow-hidden focus-within:outline-deepBlue focus-within:outline accent-deepBlue">
                  <Field
                    autoComplete="off"
                    type={passwordIsVisible ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Confirm your new password"
                    className="px-4 py-3 bg-transparent flex-1 rounded-lg focus:outline-none"
                  />
                </div>
                 <ErrorMessage name="confirmPassword" component="div" className="text-red-500" />
              </div>

              {isUpdatingAccountError && (
                <div className="text-red-500 text-left flex w-full">
                  {/* Safely access message in case error object structure varies */}
                  {(updateAccountError as any)?.message || "An error occurred"}
                </div>
              )}

              <div className="flex items-center gap-5 w-full">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="p-3 px-5 flex-1 border-red-100 border-2 rounded-lg font-bold bg-red-50/50 text-red-500 text-sm"
                >
                  Cancel
                </button>
                <button
                  disabled={!isValid || !dirty || updatingAccount}
                  type="submit"
                  className="p-3 px-5 flex-1 border-visaro-gray-700 border-2 rounded-lg font-bold bg-deepBlue text-white text-sm flex justify-center items-center"
                >
                  {updatingAccount ? "Saving..." : "Save"}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </motion.div>
  );
};

export default ChangePasswordModal;